import styled from 'styled-components';
import Color from 'color';
import {
  logoInnerColor,
  logoOuterColor,
  mobileRes,
} from 'pages/components/LFHeader/LFHeader.styled';

export const erpSidebarWidth = '240px';

const tabHoverBg = Color(logoOuterColor).alpha(0.1).toString();
const tabSelectedBg = Color(logoOuterColor).alpha(0.16).toString();
const edgeLine = Color(logoOuterColor).alpha(0.14).toString();
const erpBarBg = Color(logoInnerColor).darken(0.28).toString();

/**
 * Sibling of the bar (not a child) so transform on the bar does not trap
 * position:fixed. Sits under the bar, over the rest of the page.
 */
export const Backdrop = styled.button<{ $isVisible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1050;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: default;
  appearance: none;
  -webkit-appearance: none;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};

  @media (max-width: ${mobileRes}) {
    /* LeftPanel is the stacking context on mobile; stay under the bar (z 1100). */
    z-index: 10;
  }
`;

/**
 * Overlays the hospitality rail. Transform (not width/left) so the toggle
 * stays on the compositor and is not clipped by the rail’s overflow:hidden.
 */
export const Container = styled.aside<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: ${erpSidebarWidth};
  height: 100svh;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: ${erpBarBg};
  color: ${logoOuterColor};
  -webkit-font-smoothing: antialiased;
  transform: translateX(${({ $isVisible }) => ($isVisible ? '0' : '-100%')});
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition:
    transform 380ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0s ${({ $isVisible }) => ($isVisible ? '0s' : '380ms')};
  box-shadow: ${({ $isVisible }) => (
    $isVisible ? `8px 0 28px ${Color(erpBarBg).alpha(0.35).toString()}` : 'none'
  )};

  @media (max-width: ${mobileRes}) {
    /* Sit in the mobile drawer so it slides off with the hospitality rail.
       Above the rail (z 1000) so the ERP bar paints on top. */
    position: absolute;
    z-index: 1100;
    height: 100%;
  }
`;

export const BrandMark = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 56px;
  padding: 0 16px;
  flex-shrink: 0;
  box-sizing: border-box;
  gap: 10px;

  img {
    height: 32px !important;
    width: 32px !important;
    border-radius: 8px !important;
    filter: none !important;
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    font-size: 13px !important;
    font-weight: 700;
    letter-spacing: -0.01em !important;
    color: ${logoOuterColor} !important;
    white-space: nowrap;
    opacity: 1 !important;
    pointer-events: auto !important;
  }
`;

export const Tabs = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  overflow-y: auto;
  padding: 10px 8px 8px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Tab = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  gap: 12px;
  border: none;
  border-radius: 10px;
  background: ${({ $isSelected }) => ($isSelected ? tabSelectedBg : 'transparent')};
  color: ${logoOuterColor};
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  position: relative;
  box-sizing: border-box;
  transition: background 180ms ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${({ $isSelected }) => ($isSelected ? '60%' : '0')};
    border-radius: 0 3px 3px 0;
    background: ${logoOuterColor};
    transition: height 180ms ease;
  }

  svg {
    color: ${logoOuterColor};
    flex-shrink: 0;
    opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.82)};
  }

  span {
    font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 500)};
  }

  &:hover {
    background: ${({ $isSelected }) => ($isSelected ? tabSelectedBg : tabHoverBg)};
  }
`;

export const TabLabel = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${logoOuterColor};
`;

export const CloseRow = styled.div`
  flex-shrink: 0;
  padding: 8px 8px 14px;
  border-top: 1px solid ${edgeLine};
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  gap: 12px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${logoOuterColor};
  cursor: pointer;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  box-sizing: border-box;
  transition: background 180ms ease;

  svg {
    color: ${logoOuterColor};
    flex-shrink: 0;
  }

  &:hover {
    background: ${tabHoverBg};
  }
`;
